import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RequestItem } from '@innowise-group/core';
import { Input, Select } from '@innowise-group/mui-kit';

import { RequestFormViewProps } from '../../request-form.types';
import { FormSection } from '../form-section';

type EmploymentLocationSectionProps = Pick<
  RequestFormViewProps,
  'disabled' | 'locationCountryOptions' | 'locationCityOptions' | 'offices'
>;

const EmploymentLocationSection: React.FC<EmploymentLocationSectionProps> = ({
  disabled,
  locationCountryOptions,
  locationCityOptions,
  offices,
}) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext<RequestItem>();
  const countries = watch('countries');
  const cities = watch('cities');

  return (
    <FormSection title={t('pages.requests.creationModal.employmentLocation')}>
      <Controller
        name="countries"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={locationCountryOptions}
            disabled={disabled}
            multiple
            limitTags={1}
            disableCloseOnSelect
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.candidates.candidateCreation.country')}
                placeholder={t('pages.candidates.candidateCreation.selectCountry')}
                error={!!errors?.countries?.message}
                helperText={errors?.countries?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="cities"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={locationCityOptions}
            disabled={disabled || !countries?.length}
            multiple
            limitTags={1}
            disableCloseOnSelect
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.vacancies.creationModal.city')}
                placeholder={t('pages.vacancies.creationModal.selectTheCity')}
                error={!!errors?.cities?.message}
                helperText={errors?.cities?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="offices"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={offices || []}
            disabled={disabled || !cities?.length}
            multiple
            limitTags={1}
            disableCloseOnSelect
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.requests.creationModal.office')}
                placeholder={t('pages.requests.creationModal.specifyOffice')}
                error={!!errors?.offices?.message}
                helperText={errors?.offices?.message?.toString()}
              />
            )}
          />
        )}
      />
    </FormSection>
  );
};

export default EmploymentLocationSection;
